// src/modules/hr/pages/EmployeeProfile.jsx — Phase 14 (profile aggregates many phases)
import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Row, Col, Typography, Spin, Alert, Tag, Descriptions, Tabs, Table, Button, Statistic, Empty,
} from 'antd';
import { ArrowLeftOutlined, UserOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchEmployee } from '../api';
import {
  EMPLOYMENT_TYPE_OPTIONS, EMPLOYMENT_STATUS_OPTIONS, tagColor, tagLabel,
} from '../constants/hrConstants';

const { Title, Text } = Typography;
const money = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`;

export default function EmployeeProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [emp, setEmp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetchEmployee(id);
      setEmp(res?.data?.data || null);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load employee');
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Spin style={{ display: 'block', marginTop: 80 }} />;
  if (error) return <Alert type="error" message={error} style={{ marginTop: 24 }} />;
  if (!emp) return <Empty style={{ marginTop: 80 }} />;

  const att = emp.attendanceSummary || {};

  return (
    <div style={{ padding: '16px 0' }}>
      <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/hr/employees')} style={{ marginBottom: 12 }}>Back</Button>
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={5} style={{ margin: 0 }}><UserOutlined style={{ marginRight: 8 }} />{emp.name} {emp.employeeCode && <Text code>{emp.employeeCode}</Text>}</Title>
            <Text type="secondary">{emp.designation || '—'}{emp.department ? ` · ${emp.department}` : ''}{emp.branch?.name ? ` · ${emp.branch.name}` : ''}</Text>
          </Col>
          <Col>
            <Tag color={tagColor(EMPLOYMENT_STATUS_OPTIONS, emp.employmentStatus)}>{tagLabel(EMPLOYMENT_STATUS_OPTIONS, emp.employmentStatus)}</Tag>
            {emp.employmentType && <Tag>{tagLabel(EMPLOYMENT_TYPE_OPTIONS, emp.employmentType)}</Tag>}
          </Col>
        </Row>
      </Card>

      <Tabs
        defaultActiveKey="personal"
        items={[
          {
            key: 'personal', label: 'Personal & Employment',
            children: (
              <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                  <Descriptions title="Personal" size="small" column={1} bordered>
                    <Descriptions.Item label="Email">{emp.email}</Descriptions.Item>
                    <Descriptions.Item label="Phone">{emp.phone || '—'}</Descriptions.Item>
                    <Descriptions.Item label="DOB">{emp.dateOfBirth ? dayjs(emp.dateOfBirth).format('DD/MM/YYYY') : '—'}</Descriptions.Item>
                    <Descriptions.Item label="Gender">{emp.gender || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Marital Status">{emp.maritalStatus || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Blood Group">{emp.bloodGroup || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Aadhaar">{emp.adhar || '—'}</Descriptions.Item>
                    <Descriptions.Item label="PAN">{emp.pan || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Current Address">{emp.currentAddress || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Permanent Address">{emp.permanentAddress || '—'}</Descriptions.Item>
                  </Descriptions>
                  <Descriptions title="Family & Emergency" size="small" column={1} bordered style={{ marginTop: 16 }}>
                    <Descriptions.Item label="Father">{emp.familyDetails?.fatherName ? `${emp.familyDetails.fatherName} (${emp.familyDetails.fatherPhone || ''})` : '—'}</Descriptions.Item>
                    <Descriptions.Item label="Mother">{emp.familyDetails?.motherName ? `${emp.familyDetails.motherName} (${emp.familyDetails.motherPhone || ''})` : '—'}</Descriptions.Item>
                    <Descriptions.Item label="Spouse">{emp.familyDetails?.spouseName ? `${emp.familyDetails.spouseName} (${emp.familyDetails.spousePhone || ''})` : '—'}</Descriptions.Item>
                    {[0, 1, 2].map((i) => {
                      const ec = emp.emergencyContacts?.[i];
                      if (!ec?.name) return null;
                      return (
                        <Descriptions.Item key={i} label={`EC ${i + 1}`}>
                          {`${ec.name} (${ec.relationship || ''}) - ${ec.phone || ''}`}
                        </Descriptions.Item>
                      );
                    })}
                  </Descriptions>
                </Col>
                <Col xs={24} md={12}>
                  <Descriptions title="Employment" size="small" column={1} bordered>
                    <Descriptions.Item label="Employee Code">{emp.employeeCode || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Joining Date">{emp.joiningDate ? dayjs(emp.joiningDate).format('DD/MM/YYYY') : '—'}</Descriptions.Item>
                    <Descriptions.Item label="Confirmation Date">{emp.confirmationDate ? dayjs(emp.confirmationDate).format('DD/MM/YYYY') : '—'}</Descriptions.Item>
                    <Descriptions.Item label="Reporting Manager">{emp.reportingManager?.name || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Salary Grade">{emp.salaryGrade || '—'}</Descriptions.Item>
                    <Descriptions.Item label="PF / ESI">{(emp.pfNumber || '—')} / {(emp.esiNumber || '—')}</Descriptions.Item>
                  </Descriptions>
                  <Descriptions title="Bank Details" size="small" column={1} bordered style={{ marginTop: 16 }}>
                    <Descriptions.Item label="Account Holder">{emp.bankDetails?.accountHolderName || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Bank Name">{emp.bankDetails?.bankName || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Account Number">{emp.bankDetails?.accountNumber || '—'}</Descriptions.Item>
                    <Descriptions.Item label="IFSC Code">{emp.bankDetails?.ifsc || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Branch">{emp.bankDetails?.branchName || '—'}</Descriptions.Item>
                  </Descriptions>
                </Col>
              </Row>
            ),
          },
          {
            key: 'attendance', label: 'Attendance & Leave',
            children: (
              <>
                <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                  <Col xs={8} sm={4}><Card size="small"><Statistic title="Present" value={att.present || 0} valueStyle={{ color: '#389e0d' }} /></Card></Col>
                  <Col xs={8} sm={4}><Card size="small"><Statistic title="Absent" value={att.absent || 0} valueStyle={{ color: '#cf1322' }} /></Card></Col>
                  <Col xs={8} sm={4}><Card size="small"><Statistic title="Leave" value={att.leave || 0} /></Card></Col>
                  <Col xs={8} sm={4}><Card size="small"><Statistic title="Half Day" value={att.halfDay || 0} /></Card></Col>
                  <Col xs={8} sm={4}><Card size="small"><Statistic title="Late Marks" value={att.lateMarks || 0} valueStyle={{ color: '#fa8c16' }} /></Card></Col>
                  <Col xs={8} sm={4}><Card size="small"><Statistic title="Payable Days" value={att.payableDays || 0} /></Card></Col>
                </Row>
                <Card size="small" title="Leave Balances (this year)">
                  <Table size="small" rowKey={(r) => r._id || r.leaveType} pagination={false} dataSource={emp.leaveBalances || []}
                    columns={[
                      { title: 'Type', dataIndex: 'leaveType' },
                      { title: 'Allocated', dataIndex: 'allocated', align: 'right' },
                      { title: 'Used', dataIndex: 'used', align: 'right' },
                      { title: 'Pending', dataIndex: 'pending', align: 'right' },
                      { title: 'Available', dataIndex: 'available', align: 'right', render: (v, r) => (r.allocated || 0) - (r.used || 0) - (r.pending || 0) },
                    ]}
                    locale={{ emptyText: 'No balances yet' }}
                  />
                </Card>
              </>
            ),
          },
          {
            key: 'assets', label: `Assets (${emp.assignedAssets?.length || 0})`,
            children: (
              <Table size="small" rowKey="_id" dataSource={emp.assignedAssets || []} pagination={false}
                columns={[
                  { title: 'Code', dataIndex: 'assetCode', render: (v) => <Text code>{v}</Text> },
                  { title: 'Name', dataIndex: 'name' },
                  { title: 'Category', dataIndex: 'category', render: (v) => <Tag>{v?.replace(/_/g, ' ')}</Tag> },
                ]}
                locale={{ emptyText: 'No assets assigned' }}
              />
            ),
          },
          {
            key: 'documents', label: `Documents (${emp.documents?.length || 0})`,
            children: (
              <Table size="small" rowKey="_id" dataSource={emp.documents || []} pagination={false}
                columns={[
                  { title: 'Type', dataIndex: 'docType', render: (v) => <Tag>{v?.replace(/_/g, ' ')}</Tag> },
                  { title: 'Title', dataIndex: 'title', render: (v) => v || '—' },
                  { title: 'Expiry', dataIndex: 'expiryDate', render: (v) => v ? dayjs(v).format('DD/MM/YYYY') : '—' },
                  { title: 'File', dataIndex: 'fileUrl', render: (v) => v ? <a href={v} target="_blank" rel="noreferrer">View</a> : '—' },
                ]}
                locale={{ emptyText: 'No documents' }}
              />
            ),
          },
          {
            key: 'performance', label: 'Performance',
            children: (
              <Table size="small" rowKey="_id" dataSource={emp.performance || []} pagination={false}
                columns={[
                  { title: 'Period', dataIndex: 'periodKey' },
                  { title: 'Target', dataIndex: 'monthlyTarget', align: 'right', render: money },
                  { title: 'Revenue', dataIndex: 'revenueGenerated', align: 'right', render: money },
                  { title: 'Cases', dataIndex: 'casesClosed', align: 'right' },
                  { title: 'Gold (g)', dataIndex: 'goldPurchasedGrams', align: 'right' },
                  { title: 'Achieve %', dataIndex: 'achievementPercent', align: 'right', render: (v) => `${v || 0}%` },
                  { title: 'Rating', dataIndex: 'rating', align: 'right', render: (v) => v ? `${v}/5` : '—' },
                ]}
                locale={{ emptyText: 'No reviews' }}
              />
            ),
          },
        ]}
      />

      {emp.activeExit && (
        <Alert style={{ marginTop: 16 }} type="warning" showIcon
          message={`Active exit process: ${emp.activeExit.reference} (${emp.activeExit.status})`}
          description={emp.activeExit.lastWorkingDate ? `Last working date: ${dayjs(emp.activeExit.lastWorkingDate).format('DD/MM/YYYY')}` : null}
        />
      )}
    </div>
  );
}
